import type * as React from "react";

import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import {
  useDataTableDensity,
  useDataTableFooterChrome,
} from "./data-table-context";
import { DENSITY_CONFIG } from "./data-table-utils";
import type { Omittable } from "../../lib/props";

export type DataTableAddRowProps = Omittable<{
  /** Row label, e.g. "Add Project" / "Create Field". */
  label: string;
  /** Omit when the row is a popover trigger — the wrapping trigger owns the
   *  press (e.g. an entity selector rendered around this row). */
  onClick?: () => void;
  /**
   * Extra px past the first column's content line — a table whose rows carry
   * their own leading gutter (e.g. expand chevrons) passes that gutter width
   * so the plus lands on the rows' content line, not the gutter line.
   */
  indentPx?: number;
  "aria-label"?: string;
  /** Merged by a wrapping popover trigger (`render={<DataTableAddRow/>}`). */
  ref?: React.Ref<HTMLButtonElement>;
}>;

/**
 * The "+ Add …" affordance as a REAL table row (the reference add-row): full
 * width, row height from the table's density, the ROW-tier hover wash across
 * the whole strip, and content on the first column's content line — the
 * footer-chrome context accounts for paddingX and the selection column, so
 * the row stays aligned whatever chrome the table carries. Render it from
 * `groupFooter`.
 */
export function DataTableAddRow({
  label,
  onClick,
  indentPx = 0,
  "aria-label": ariaLabel,
  ...props
}: DataTableAddRowProps) {
  const density = useDataTableDensity();
  const { contentLeadingPx } = useDataTableFooterChrome();
  const densityConfig = DENSITY_CONFIG[density];

  return (
    <Box<"button">
      as="button"
      direction="row"
      align="center"
      height={densityConfig.rowHeight}
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      // Row-tier hover (60% of --wash-hover), matching TableRow — the strip
      // presents as a row, and the full control wash would hover louder than
      // the data rows above it.
      className={`w-full cursor-pointer hover:bg-[color-mix(in_oklab,var(--wash-hover)_60%,transparent)] ${densityConfig.textSize}`}
      {...props}
    >
      {/* Leading spacer, not padding: dynamic px from the chrome context. */}
      <Box width={contentLeadingPx + indentPx} shrink={false} />
      <Box direction="row" align="center" gap="sm">
        <Icon name="plus" size="md" color="muted" />
        <Text size={null} variant="muted">
          {label}
        </Text>
      </Box>
    </Box>
  );
}
